export type MatchSummaryCounts = {
  loves: number;
  likes: number;
  notForMe: number;
  total: number;
};

export type ActiveCollaborationSummary = {
  collaborationId: string;
  designerId: string;
  status: string;
  startedAt?: string | null;
  endRequestedAt?: string | null;
  endRequestedBy?: string | null;
};

export type MatchSummaryResponse = {
  counts: MatchSummaryCounts;
  matchingPaused: boolean;
  activeCollaboration: ActiveCollaborationSummary | null;
};

export type ActiveCollaborationResponse = {
  collaboration: {
    _id: string;
    homeOwnerId: string;
    designerId: string;
    status: string;
    startedAt?: string | null;
    endRequestedAt?: string | null;
    endRequestedBy?: string | null;
    endNote?: string | null;
  } | null;
  designer: {
    _id: string;
    aliasId?: string | null;
    name?: string | null;
    profilePicUrl?: string | null;
    averageRating?: number | null;
  } | null;
  homeowner: {
    _id: string;
    name?: string | null;
  } | null;
};

export type CollaborationFeedbackInput = {
  projectId?: string | null;
  rating: number;
  review?: string | null;
  platformFeedback?: string | null;
};

export type ProjectSummaryResponse = {
  projectId: string;
  designer: {
    id: string;
    name?: string | null;
    aliasId?: string | null;
    profilePicUrl?: string | null;
  } | null;
  feedback: {
    rating: number;
    review?: string | null;
    platformFeedback?: string | null;
    submittedAt?: string | null;
  } | null;
};

export type DesignerProjectRow = {
  projectId: string;
  projectName?: string | null;
  homeownerId: string;
  homeownerName?: string | null;
  homeownerAvatarUrl?: string | null;
  matchedAt?: string | null;
  status: "not_started" | "in_progress" | "completed" | "cancelled";
  completionRequestedBy?: "homeowner" | "designer" | null;
  completionRequestedAt?: string | null;
  completionConfirmedAt?: string | null;
  completionConfirmedBy?: "homeowner" | "designer" | null;
};

const getApiUrl = () => {
  return (
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === "development"
      ? "http://localhost:8080"
      : "https://hdg-api-service-485830218751.us-central1.run.app")
  );
};

export const fetchMatchSummary = async (userId: string): Promise<MatchSummaryResponse> => {
  const url = new URL(`${getApiUrl()}/api/v1/matches/summary`);
  url.searchParams.set("userId", userId);

  const response = await fetch(url.toString(), { credentials: "include" });
  if (!response.ok) {
    throw new Error("Unable to load match summary");
  }

  return (await response.json()) as MatchSummaryResponse;
};

export const selectPerfectMatch = async (
  matchId: string,
  userId: string,
  profileId?: string | null
) => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/matches/${encodeURIComponent(matchId)}/select`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, profileId }),
    }
  );

  if (!response.ok) {
    let message = "Unable to select perfect match";
    try {
      const data = (await response.json()) as { error?: string };
      if (data?.error) {
        message = data.error;
      }
    } catch {
      // Keep default message when response body is not JSON.
    }
    throw new Error(message);
  }

  return response.json();
};

export const fetchActiveCollaboration = async (
  userId: string,
  role?: string | null
): Promise<ActiveCollaborationResponse> => {
  const url = new URL(`${getApiUrl()}/api/v1/collaborations/active`);
  url.searchParams.set("userId", userId);
  if (role) {
    url.searchParams.set("role", role);
  }

  const response = await fetch(url.toString(), { credentials: "include" });
  if (!response.ok) {
    throw new Error("Unable to load collaboration");
  }

  return (await response.json()) as ActiveCollaborationResponse;
};

export const requestEndCollaboration = async (
  collaborationId: string,
  userId: string,
  note?: string | null
) => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/collaborations/${encodeURIComponent(
      collaborationId
    )}/end-request`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, note }),
    }
  );

  if (!response.ok) {
    throw new Error("Unable to request end collaboration");
  }

  return response.json();
};

export const confirmEndCollaboration = async (
  collaborationId: string,
  userId: string
) => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/collaborations/${encodeURIComponent(
      collaborationId
    )}/end-confirm`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    }
  );

  if (!response.ok) {
    throw new Error("Unable to confirm end collaboration");
  }

  return response.json();
};

export const submitCollaborationFeedback = async (
  collaborationId: string,
  userId: string,
  payload: CollaborationFeedbackInput
) => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/collaborations/${encodeURIComponent(
      collaborationId
    )}/feedback`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, ...payload }),
    }
  );

  if (!response.ok) {
    throw new Error("Unable to submit feedback");
  }

  return response.json();
};

export const fetchProjectSummary = async (
  projectId: string,
  userId: string
): Promise<ProjectSummaryResponse> => {
  const url = new URL(`${getApiUrl()}/api/v1/projects/${encodeURIComponent(projectId)}/summary`);
  url.searchParams.set("userId", userId);

  const response = await fetch(url.toString(), { credentials: "include" });
  if (!response.ok) {
    throw new Error("Unable to load project summary");
  }

  return (await response.json()) as ProjectSummaryResponse;
};

export const fetchDesignerProjects = async (
  designerId: string
): Promise<{ projects: DesignerProjectRow[] }> => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/designers/${encodeURIComponent(designerId)}/projects`,
    {
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Unable to load designer projects");
  }

  return (await response.json()) as { projects: DesignerProjectRow[] };
};
